import { useAtomValue } from 'jotai';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActivityCleanFilterIcon } from '@/lib/icon';
import { Button, DateInput, Dropdown, Flex, IconSvg, InputSearch } from '@/lib/v2/components';
import { eventOnChangeDateInput } from '@/lib/v2/components/DateInput/DateInput';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';
import { StateIcon } from '@/lib/v2/icons/outline';
import { ChannelIcon } from '@/lib/v2/icons/solid';

import { atomDetailCampaign } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { DROPDOWN_ACTIONS_STATE, DROPDOWN_CHANNELS } from '@/modules/CampaignsModule/constants';
import {
  useCampaignActionsMutationContext,
  useCampaignActionsStateContext,
} from '@/modules/CampaignsModule/contexts/CampaignActionsContext';
import { ECampaignsTypeOptions } from '@/modules/CampaignsModule/interfaces/Campaigns';
import { clearObjectFalsy } from '@/modules/CampaignsModule/utils';

const FilterBar = () => {
  const { t } = useTranslation();
  const detailCampaign = useAtomValue(atomDetailCampaign);

  const { filterBy, clearFilterBy } = useCampaignActionsStateContext();
  const { setFilterBy, setClearFilterBy, resetPagination } = useCampaignActionsMutationContext();

  const [querySearch, setQuerySearch] = useState<string>(filterBy?.search ?? '');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    filterBy?.startDate ?? null,
    filterBy?.endDate ?? null,
  ]);

  const handleOnClickSearch = useCallback(() => {
    const currentFilterBy = { ...filterBy, search: querySearch };
    const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

    setFilterBy(clearedFiltersBy);
    resetPagination();
  }, [filterBy, querySearch, resetPagination, setFilterBy]);

  const handleOnChangeSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuerySearch(event.target.value);
  }, []);

  const handleOnClearSearch = useCallback(() => {
    setQuerySearch('');
    const currentFilterBy = { ...filterBy, search: '' };
    const clearedFiltersBy = clearObjectFalsy(currentFilterBy);
    setFilterBy(clearedFiltersBy);
  }, [filterBy, setFilterBy]);

  const handleOnClickState = useCallback(
    (data: DropdownItemProps[]) => {
      const stateID: number[] = data.map((item) => item.value as number);
      const currentFilterBy = { ...filterBy, status: stateID };
      const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

      setFilterBy(clearedFiltersBy);
      resetPagination();
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleOnClickChannel = useCallback(
    (data: DropdownItemProps[]) => {
      const channelID: number[] = data.map((item) => item.value as number);
      const currentFilterBy = { ...filterBy, channel: channelID };
      const clearedFiltersBy = clearObjectFalsy(currentFilterBy);

      setFilterBy(clearedFiltersBy);
      resetPagination();
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleOnChangeDate: eventOnChangeDateInput = useCallback(
    (event) => {
      if (Array.isArray(event)) {
        setDateRange(event as [Date | null, Date | null]);
        if (event[0] && event[1]) {
          setFilterBy({ ...filterBy, startDate: event[0], endDate: event[1] });
          resetPagination();
        }
      }
    },
    [filterBy, resetPagination, setFilterBy]
  );

  const handleReset = useCallback(() => {
    setQuerySearch('');
    setDateRange([null, null]);
    setFilterBy({});
    resetPagination();
  }, [resetPagination, setFilterBy]);

  useEffect(() => {
    if (clearFilterBy) {
      handleReset();
      setClearFilterBy(false);
    }
  }, [clearFilterBy, handleReset, setClearFilterBy]);

  return (
    <Flex withGap alignment="start" className="wrapperFilterBar z-10 mb-[-72px] max-w-fit px-4">
      <div className="w-72">
        <InputSearch
          autoComplete="off"
          id="search-campaigns-action"
          placeHolder={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.searchPlaceholder')}
          value={querySearch}
          onAction={handleOnClickSearch}
          onChange={handleOnChangeSearch}
          onClear={handleOnClearSearch}
        />
      </div>
      <Flex noGrow withGap>
        <div className="min-w-[130px]">
          <Dropdown
            checkBox
            checkboxContainerWithScroll
            fullWidth
            withSearch
            buttonOnClick={handleOnClickState}
            buttonText={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.button')}
            dropdownItems={DROPDOWN_ACTIONS_STATE.map((state) => ({
              ...state,
              label: t(`CAMPAIGN_ACTIONS_MAIN.STATES.${state.label}`),
            })).sort((a, b) => a.label.localeCompare(b.label))}
            icon={<IconSvg svgComponent={<StateIcon />} />}
            id="state-campaigns-action"
            label={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.state')}
          />
        </div>
      </Flex>
      {detailCampaign.campaignTypeId === ECampaignsTypeOptions.MANUAL && (
        <Flex noGrow withGap>
          <div className="min-w-[145px]">
            <Dropdown
              checkBox
              fullWidth
              buttonOnClick={handleOnClickChannel}
              buttonText={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.button')}
              dropdownItems={DROPDOWN_CHANNELS.map((channel) => ({
                ...channel,
                label: t(`CAMPAIGN_ACTIONS_MAIN.CHANNEL.${channel.label}`),
              })).sort((a, b) => a.label.localeCompare(b.label))}
              icon={<IconSvg svgComponent={<ChannelIcon />} />}
              id="channels-campaigns-action"
              label={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.channel')}
            />
          </div>
        </Flex>
      )}
      <Flex noGrow withGap>
        <div>
          <DateInput
            isRequired
            selectsRange
            endDate={dateRange[1]}
            id="date-campaigns-action"
            name="date"
            placeholder={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.date')}
            startDate={dateRange[0]}
            onChange={handleOnChangeDate}
          />
        </div>
      </Flex>
      <div className="w-auto">
        <Button
          fullWidth
          standard
          disabled={Object.keys(filterBy).length === 0}
          iconLeft={<IconSvg fillColor="primary" svgComponent={<ActivityCleanFilterIcon />} />}
          id="clear-filters-button"
          tooltip={t('CAMPAIGN_ACTIONS_MAIN.FILTERS.buttonClear')}
          onClick={handleReset}
        ></Button>
      </div>
    </Flex>
  );
};

export default FilterBar;
